/**
 * Видеонаблюдение и видеоаналитика (раздел 6.31 ТЗ, ВН-01–09), каркас первой очереди.
 *
 * Видеосервер и модель стоят на площадке или в дата-центре заказчика: в портал приходят только состояние камер,
 * события аналитики и кадры. Портал хранит карточки камер, события с решением человека, журнал просмотров,
 * положение о видеонаблюдении и письменные согласия на биометрию.
 */
import { z } from 'zod';

const Uuid = z.uuid();
const DateStr = z.iso.date();

/* ───────────────────────── Справочники ───────────────────────── */

export const CAMERA_PURPOSES = {
  entry: 'Въезд, КПП',
  work_zone: 'Зона работ',
  warehouse: 'Склад',
  camp: 'Бытовой городок',
  other: 'Другое',
} as const;
export type CameraPurpose = keyof typeof CAMERA_PURPOSES;

export const CAMERA_SOURCES = {
  rtsp: 'RTSP-камера',
  onvif: 'ONVIF-камера',
  nvr: 'Регистратор (NVR)',
  cloud: 'Облачная камера оператора',
} as const;
export type CameraSource = keyof typeof CAMERA_SOURCES;

/** Состояние камеры по последнему сигналу видеосервера (ВН-01) */
export const CAMERA_STATES = {
  online: 'В сети',
  no_signal: 'Нет сигнала',
  not_connected: 'Ждёт подключения',
} as const;
export type CameraState = keyof typeof CAMERA_STATES;

/** События аналитики (ВН-03–07) */
export const VIDEO_EVENT_KINDS = {
  ppe_helmet: 'Без каски',
  ppe_vest: 'Без жилета',
  danger_zone: 'Вход в опасную зону',
  no_harness: 'Работа на высоте без страховки',
  fall: 'Падение человека',
  smoke_fire: 'Дым или огонь',
  intruder: 'Посторонний в нерабочее время',
  vehicle_plate: 'Номер на въезде или выезде',
  person_pass: 'Проход человека',
  progress: 'Кадр прогресса работ',
  idle: 'Простой техники или скопление людей',
  other: 'Другое',
} as const;
export type VideoEventKind = keyof typeof VIDEO_EVENT_KINDS;

/** Срочные события идут в «Ждёт меня» первыми (ВН-08, УВ-05) */
export const URGENT_EVENT_KINDS: VideoEventKind[] = ['fall', 'smoke_fire', 'danger_zone', 'no_harness'];

/** События по технике безопасности: из них создаётся замечание ОТ и ПБ (поток 7) */
export const SAFETY_EVENT_KINDS: VideoEventKind[] = [
  'ppe_helmet',
  'ppe_vest',
  'danger_zone',
  'no_harness',
  'fall',
  'smoke_fire',
  'intruder',
];

export const EVENT_DECISIONS = {
  confirmed: 'Подтверждено',
  false_positive: 'Ложное срабатывание',
  ignored: 'Не требует действий',
} as const;
export type EventDecision = keyof typeof EVENT_DECISIONS;

export const VIDEO_VIEW_KINDS = {
  live: 'Живой просмотр',
  archive: 'Архив',
  event_frame: 'Кадр события',
} as const;
export type VideoViewKind = keyof typeof VIDEO_VIEW_KINDS;

/* ───────────────────────── Камеры ───────────────────────── */

/** Адрес потока с логином и паролем в портал не вносится: он остаётся на видеосервере */
const ExternalRef = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .refine(
    (v) => !/[:@/\\]/.test(v),
    'Укажите имя или номер канала на видеосервере, а не адрес потока с паролем',
  );

export const CameraInput = z.strictObject({
  projectId: Uuid,
  objectId: Uuid.nullable().optional(),
  name: z.string().trim().min(2).max(120),
  purpose: z.enum(Object.keys(CAMERA_PURPOSES) as [CameraPurpose, ...CameraPurpose[]]),
  direction: z.string().trim().max(200).nullable().optional(),
  planPlace: z.string().trim().max(200).nullable().optional(),
  planX: z.number().min(0).max(1).nullable().optional(),
  planY: z.number().min(0).max(1).nullable().optional(),
  sourceKind: z.enum(Object.keys(CAMERA_SOURCES) as [CameraSource, ...CameraSource[]]),
  externalRef: ExternalRef.nullable().optional(),
  responsibleId: Uuid.nullable().optional(),
  signPosted: z.boolean().optional(),
});
export type CameraInput = z.infer<typeof CameraInput>;

export const CameraPatch = CameraInput.omit({ projectId: true }).partial().extend({
  /** Распознавание лиц на этой камере: только администратор, только при утверждённом положении (ВН-05, ВН-09) */
  biometricsEnabled: z.boolean().optional(),
});
export type CameraPatch = z.infer<typeof CameraPatch>;

export const CameraView = z.object({
  id: Uuid,
  projectId: Uuid,
  projectCode: z.string(),
  objectId: Uuid.nullable(),
  objectName: z.string().nullable(),
  name: z.string(),
  purpose: z.string(),
  direction: z.string().nullable(),
  planPlace: z.string().nullable(),
  planX: z.number().nullable(),
  planY: z.number().nullable(),
  sourceKind: z.string(),
  externalRef: z.string().nullable(),
  responsibleId: Uuid.nullable(),
  responsibleName: z.string().nullable(),
  signPosted: z.boolean(),
  biometricsEnabled: z.boolean(),
  state: z.enum(['online', 'no_signal', 'not_connected']),
  lastSeenAt: z.string().nullable(),
  archived: z.boolean(),
  pendingEvents: z.number().int(),
});
export type CameraView = z.infer<typeof CameraView>;

/** Живой просмотр и архив: портал пишет просмотр в журнал, поток отдаёт видеосервер заказчика */
export const ViewRequest = z.strictObject({
  kind: z.enum(['live', 'archive']),
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
});
export type ViewRequest = z.infer<typeof ViewRequest>;

/* ───────────────────────── События ───────────────────────── */

export const EventDecisionInput = z.strictObject({
  decision: z.enum(Object.keys(EVENT_DECISIONS) as [EventDecision, ...EventDecision[]]),
  note: z.string().trim().max(1000).nullable().optional(),
});
export type EventDecisionInput = z.infer<typeof EventDecisionInput>;

export const EventRemarkInput = z.strictObject({
  kind: z.enum(['prescription', 'remark', 'instruction']).default('remark'),
  assigneeId: Uuid.nullable().optional(),
  dueDate: DateStr.optional(),
});
export type EventRemarkInput = z.infer<typeof EventRemarkInput>;

export const VideoEventView = z.object({
  id: Uuid,
  projectId: Uuid,
  projectCode: z.string(),
  cameraId: Uuid,
  cameraName: z.string(),
  kind: z.string(),
  occurredAt: z.string(),
  confidence: z.number().nullable(),
  urgent: z.boolean(),
  hasFrame: z.boolean(),
  plate: z.string().nullable(),
  personCount: z.number().int().nullable(),
  personName: z.string().nullable(),
  identityDropped: z.boolean(),
  modelName: z.string().nullable(),
  source: z.string(),
  decision: z.string().nullable(),
  decidedAt: z.string().nullable(),
  decidedByName: z.string().nullable(),
  decisionNote: z.string().nullable(),
  remarkId: Uuid.nullable(),
  remarkNumber: z.string().nullable(),
});
export type VideoEventView = z.infer<typeof VideoEventView>;

/* ───────────────────────── Настройки, согласия, ключи ───────────────────────── */

export const DEFAULT_VIDEO_SETTINGS = {
  archiveDays: 30,
  eventDays: 365,
  offlineAfterMinutes: 5,
};

export const VideoSettingsInput = z.strictObject({
  archiveDays: z.number().int().min(1).max(3650),
  eventDays: z.number().int().min(1).max(3650),
  offlineAfterMinutes: z.number().int().min(1).max(1440),
  /** Дата утверждения положения о видеонаблюдении компании; без неё биометрия не включается */
  policyApprovedOn: DateStr.nullable(),
  policyNote: z.string().trim().max(2000).nullable().optional(),
});
export type VideoSettingsInput = z.infer<typeof VideoSettingsInput>;

export const ConsentInput = z.strictObject({
  userId: Uuid,
  signedOn: DateStr,
  /** Скан подписанного согласия, загруженный в файлы портала */
  documentFileId: Uuid,
});
export type ConsentInput = z.infer<typeof ConsentInput>;

export const ConsentRevokeInput = z.strictObject({
  reason: z.string().trim().min(3).max(500),
});
export type ConsentRevokeInput = z.infer<typeof ConsentRevokeInput>;

export const IngestKeyInput = z.strictObject({ name: z.string().trim().min(2).max(120) });
export type IngestKeyInput = z.infer<typeof IngestKeyInput>;

/* ───────────────────────── Приём от видеосервера ───────────────────────── */

export const IngestHeartbeat = z.strictObject({
  cameras: z
    .array(
      z.strictObject({
        ref: ExternalRef,
        online: z.boolean(),
      }),
    )
    .min(1)
    .max(500),
});
export type IngestHeartbeat = z.infer<typeof IngestHeartbeat>;

export const IngestEvent = z.strictObject({
  ref: ExternalRef,
  kind: z.enum(Object.keys(VIDEO_EVENT_KINDS) as [VideoEventKind, ...VideoEventKind[]]),
  occurredAt: z.iso.datetime({ offset: true }),
  confidence: z.number().min(0).max(1).nullable().optional(),
  plate: z.string().trim().max(20).nullable().optional(),
  personCount: z.number().int().min(0).max(10_000).nullable().optional(),
  /** Опознанный человек: принимается только при письменном согласии и включённой биометрии камеры */
  personUserId: Uuid.nullable().optional(),
  modelName: z.string().trim().max(100).nullable().optional(),
  /** Кадр JPEG в base64, до 1,5 МБ */
  frameBase64: z.string().max(2_100_000).nullable().optional(),
});
export type IngestEvent = z.infer<typeof IngestEvent>;

/** Номер кадра дня для отчёта смены (поток 6) */
export const DayFrame = z.object({
  eventId: Uuid,
  cameraId: Uuid,
  cameraName: z.string(),
  kind: z.string(),
  occurredAt: z.string(),
});
export type DayFrame = z.infer<typeof DayFrame>;
